import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  KnowledgeApp,
  type KnowledgeAppNotice,
} from "@note-app/app-shell";
import {
  createBrowserPlatformRuntime,
  registerPwaWorker,
  type PlatformRuntime,
} from "@note-app/platform-runtime";
import "@note-app/ui/styles.css";
import "@note-app/app-shell/styles.css";
import "./web.css";
import { shouldAutoApplyPwaUpdate } from "./pwa-update-policy.ts";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function WebRoot() {
  const [runtime, setRuntime] = useState<PlatformRuntime>();
  const [error, setError] = useState<unknown>();
  const [notices, setNotices] = useState<KnowledgeAppNotice[]>([]);

  useEffect(() => {
    let active = true;
    let opened: PlatformRuntime | undefined;
    void createBrowserPlatformRuntime({
      name: "fengyue-notes",
      platform: "web",
      onBlocked: () => {
        setNotices((current) =>
          upsertNotice(current, {
            id: "database-blocked",
            tone: "warning",
            title: "请关闭其他丰月笔记标签页",
            description: "数据库升级正等待旧标签页释放连接。",
          }),
        );
      },
      onVersionChange: () => {
        setNotices((current) =>
          upsertNotice(current, {
            id: "database-version",
            tone: "warning",
            title: "本地数据库已在其他窗口升级",
            description: "重新载入前，此窗口不会继续写入。",
            actionLabel: "重新载入",
            onAction: () => location.reload(),
          }),
        );
      },
    }).then(
      async (created) => {
        opened = created;
        if (!active) {
          await created.close();
          return;
        }
        setRuntime(created);
        try {
          const health = await created.storageHealth.requestPersistence();
          if (!health.persisted) {
            setNotices((current) =>
              upsertNotice(current, {
                id: "storage-risk",
                tone: "warning",
                title: "本机数据可能被浏览器回收",
                description: "浏览器未授予持久存储。请定期导出 .notebundle 完整备份。",
              }),
            );
          }
        } catch {
          setNotices((current) =>
            upsertNotice(current, {
              id: "storage-health-unavailable",
              tone: "warning",
              title: "无法读取存储持久性状态",
              description: "数据仍会写入 IndexedDB；建议立即导出备份。",
            }),
          );
        }
      },
      (cause: unknown) => {
        if (active) setError(cause);
      },
    );
    return () => {
      active = false;
      void opened?.close();
    };
  }, []);

  useEffect(() => {
    let installEvent: BeforeInstallPromptEvent | undefined;
    const clearInstallNotice = () => {
      installEvent = undefined;
      setNotices((current) =>
        current.filter((notice) => notice.id !== "install"),
      );
    };
    const handleInstall = (event: Event) => {
      event.preventDefault();
      installEvent = event as BeforeInstallPromptEvent;
      setNotices((current) =>
        upsertNotice(current, {
          id: "install",
          tone: "info",
          title: "安装丰月笔记",
          description: "安装后可像普通应用一样从桌面启动。",
          actionLabel: "安装应用",
          onAction: async () => {
            const prompt = installEvent;
            if (!prompt) return;
            try {
              await prompt.prompt();
              await prompt.userChoice;
            } finally {
              clearInstallNotice();
            }
          },
        }),
      );
    };
    const handleOnline = () =>
      setNotices((current) =>
        current.filter((notice) => notice.id !== "offline"),
      );
    const handleOffline = () =>
      setNotices((current) =>
        upsertNotice(current, {
          id: "offline",
          tone: "info",
          title: "已离线",
          description: "核心个人流程仍可使用，改动会保存在本机。",
        }),
      );
    window.addEventListener("beforeinstallprompt", handleInstall);
    window.addEventListener("appinstalled", clearInstallNotice);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    if (!navigator.onLine) handleOffline();
    return () => {
      window.removeEventListener("beforeinstallprompt", handleInstall);
      window.removeEventListener("appinstalled", clearInstallNotice);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    // Vite injects styles and serves the application shell directly in
    // development; registering a production worker there would cache a false
    // `/sw.js` HTML response and obscure real UI failures with a noisy warning.
    if (!import.meta.env.PROD) return;
    let active = true;
    let updateCheckTimer: number | undefined;
    let unregisterVisibilityCheck: (() => void) | undefined;
    let applyingUpdate = false;
    const autoApplyUpdate = shouldAutoApplyPwaUpdate(window.location);
    const reportWorkerError = (cause: unknown) => {
      if (!active) return;
      setNotices((current) =>
        upsertNotice(current, {
          id: "worker-error",
          tone: "warning",
          title: "离线应用壳未能更新",
          description: cause instanceof Error ? cause.message : "可继续在线使用。",
        }),
      );
    };
    const applyUpdate = async (activate: () => Promise<void>) => {
      if (applyingUpdate) return;
      applyingUpdate = true;
      setNotices((current) =>
        current.filter((notice) => notice.id !== "update"),
      );
      try {
        await activate();
      } catch (cause) {
        applyingUpdate = false;
        reportWorkerError(cause);
      }
    };

    void registerPwaWorker({
      onOfflineReady: () =>
        active &&
        setNotices((current) =>
          upsertNotice(current, {
            id: "offline-ready",
            tone: "success",
            title: "离线资源已就绪",
            description: "关闭网络后仍可打开应用壳和本地知识库。",
          }),
        ),
      onUpdateReady: (activate) => {
        if (!active) return;
        if (autoApplyUpdate) {
          void applyUpdate(activate);
          return;
        }
        setNotices((current) =>
          upsertNotice(current, {
            id: "update",
            tone: "info",
            title: "新版本已下载",
            description: "保存当前编辑后即可安全切换，上一版应用壳仍可用于回滚。",
            actionLabel: "安全更新",
            onAction: () => applyUpdate(activate),
          }),
        );
      },
      onError: reportWorkerError,
    }).then((pwa) => {
      if (!pwa || !active) return;
      const checkForUpdate = () => {
        if (!active || document.visibilityState !== "visible") return;
        void pwa.checkForUpdate().catch(reportWorkerError);
      };
      const handleVisibilityChange = () => checkForUpdate();
      document.addEventListener("visibilitychange", handleVisibilityChange);
      unregisterVisibilityCheck = () =>
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      checkForUpdate();
      if (autoApplyUpdate) {
        updateCheckTimer = window.setInterval(checkForUpdate, 15_000);
      }
    });

    return () => {
      active = false;
      unregisterVisibilityCheck?.();
      if (updateCheckTimer !== undefined) {
        window.clearInterval(updateCheckTimer);
      }
    };
  }, []);

  if (error) {
    return (
      <main className="web-recovery" role="alert">
        <span aria-hidden="true">丰</span>
        <h1>丰月笔记未能打开本地知识库</h1>
        <p>{error instanceof Error ? error.message : "浏览器拒绝了本地存储访问。"}</p>
        <p>请退出隐私模式、释放设备空间或关闭其他标签页，再重试。不要清除站点数据，除非已有备份。</p>
        <button type="button" onClick={() => location.reload()}>重新尝试</button>
      </main>
    );
  }
  if (!runtime) {
    return (
      <main className="web-boot" aria-busy="true">
        <span aria-hidden="true">丰</span>
        <p>正在打开离线知识库…</p>
      </main>
    );
  }
  return <KnowledgeApp runtime={runtime} notices={notices} />;
}

function upsertNotice(
  notices: KnowledgeAppNotice[],
  notice: KnowledgeAppNotice,
): KnowledgeAppNotice[] {
  return [...notices.filter((item) => item.id !== notice.id), notice];
}

const root = document.getElementById("root");
if (!root) throw new Error("Missing #root");
createRoot(root).render(
  <StrictMode>
    <WebRoot />
  </StrictMode>,
);
